import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert, Drawer,
  Chip, Divider, IconButton, Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReactECharts from 'echarts-for-react';
import { useParams, useNavigate } from 'react-router-dom';
import { nodeNetworkService } from '../services/nodeNetworkService';
import { NODE_TYPE_COLORS, NODE_ROLE_LABELS, CONNECTION_TYPE_COLORS } from '../constants/nodeTypes';

function NodeNetworkGraph() {
  const { networkId } = useParams();
  const navigate = useNavigate();
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    loadGraph();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkId]);

  const loadGraph = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await nodeNetworkService.getNetworkGraph(networkId);
      setGraphData(data ?? null);
    } catch (err) {
      setError('Failed to load graph: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const buildChartOption = useCallback(() => {
    if (!graphData) return {};

    const nodes = graphData?.nodes ?? [];
    const edges = graphData?.edges ?? [];
    const maxLedgers = Math.max(...nodes.map(n => n.ledger_count ?? 0), 1);

    const chartNodes = nodes.map(n => {
      const isPactSynced = n.pact_sync_status === 'synced';
      const isPactPending = n.pact_sync_status === 'pending';
      const isPactError = n.pact_sync_status === 'error';
      return {
        id: n.id,
        name: n.name,
        symbolSize: 20 + Math.round((n.ledger_count / maxLedgers) * 40),
        itemStyle: {
          color: NODE_TYPE_COLORS[n.node_type] || '#90a4ae',
          borderColor: isPactSynced ? '#4caf50' : isPactError ? '#f44336' : isPactPending ? '#ff9800' : n.bound_identity ? '#ffd600' : '#ffffff',
          borderWidth: isPactSynced ? 4 : isPactPending || isPactError ? 3 : n.bound_identity ? 3 : 1
        },
        label: {
          show: true,
          position: 'bottom',
          fontSize: 11
        },
        tooltip: {
          formatter: `${n.name}<br/>Type: ${n.node_type}<br/>Role: ${NODE_ROLE_LABELS[n.node_role] || n.node_role}<br/>Ledgers: ${n.ledger_count}${isPactSynced ? '<br/>PACT: Synced #' + n.pact_node_id : ''}${isPactPending ? '<br/>PACT: Pending' : ''}${isPactError ? '<br/>PACT: Error' : ''}`
        },
        rawData: n
      };
    });

    const chartEdges = edges.map(e => {
      const isPactSynced = e.pact_sync_status === 'synced';
      const isPactPending = e.pact_sync_status === 'pending';
      return {
        source: e.source,
        target: e.target,
        lineStyle: {
          color: CONNECTION_TYPE_COLORS[e.connection_type] || '#78909c',
          width: isPactSynced ? 3 : 2,
          curveness: 0.1,
          ...(isPactSynced ? { type: 'solid', shadowBlur: 4, shadowColor: 'rgba(76,175,80,0.4)' } : {}),
          ...(isPactPending ? { type: 'dashed' } : {})
        },
        label: {
          show: isPactSynced || isPactPending,
          formatter: isPactSynced ? 'PACT' : 'pending',
          fontSize: 9,
          color: isPactSynced ? '#4caf50' : '#ff9800'
        },
        rawData: e
      };
    });

    return {
      tooltip: { trigger: 'item' },
      legend: [{
        data: Object.keys(NODE_TYPE_COLORS),
        orient: 'vertical',
        right: 10,
        top: 'center',
        formatter: name => name.charAt(0).toUpperCase() + name.slice(1)
      }],
      series: [{
        type: 'graph',
        layout: 'force',
        data: chartNodes,
        links: chartEdges,
        roam: true,
        draggable: true,
        force: {
          repulsion: 200,
          edgeLength: [80, 200],
          gravity: 0.1
        },
        emphasis: {
          focus: 'adjacency',
          lineStyle: { width: 4 }
        }
      }]
    };
  }, [graphData]);

  const handleChartClick = (params) => {
    if (params.dataType === 'node') {
      setSelectedNode(params.data.rawData);
      setDrawerOpen(true);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            {graphData?.network?.network_name}
          </Typography>
          <Chip
            label={graphData?.network?.network_type?.replace('_', ' ')}
            size="small"
            color="primary"
            sx={{ mt: 0.5 }}
          />
        </Box>
        <Button variant="outlined" size="small" onClick={() => navigate('/node-network')}>
          Back to Networks
        </Button>
      </Box>

      <Box display="flex" gap={2} mb={1} flexWrap="wrap">
        <Typography variant="caption" color="text.secondary">
          {graphData?.nodes?.length} nodes · {graphData?.edges?.length} connections
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Click a node to see details · Drag to rearrange
        </Typography>
      </Box>

      {/* Connection type legend */}
      <Box display="flex" gap={1} mb={2} flexWrap="wrap">
        {Object.entries(CONNECTION_TYPE_COLORS).map(([type, color]) => (
          <Box key={type} display="flex" alignItems="center" gap={0.5}>
            <Box sx={{ width: 16, height: 3, backgroundColor: color, borderRadius: 1 }} />
            <Typography variant="caption">{type.replace('_', ' ')}</Typography>
          </Box>
        ))}
        <Box display="flex" alignItems="center" gap={0.5} sx={{ ml: 1 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: '3px solid #4caf50', backgroundColor: 'transparent' }} />
          <Typography variant="caption">PACT Synced</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: '3px solid #ff9800', backgroundColor: 'transparent' }} />
          <Typography variant="caption">PACT Pending</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: '3px solid #f44336', backgroundColor: 'transparent' }} />
          <Typography variant="caption">PACT Error</Typography>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <ReactECharts
          option={buildChartOption()}
          style={{ height: '600px', width: '100%' }}
          onEvents={{ click: handleChartClick }}
        />
      </Paper>

      {/* Selected Node Sidebar */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 320, p: 2 } }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6">Node Details</Typography>
          <IconButton onClick={() => setDrawerOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        {selectedNode && (
          <>
            <Typography variant="subtitle1" fontWeight="bold">{selectedNode.name}</Typography>
            <Box display="flex" gap={0.5} mt={0.5} mb={1} flexWrap="wrap">
              <Chip label={selectedNode.node_type} size="small" sx={{ backgroundColor: NODE_TYPE_COLORS[selectedNode.node_type], color: '#fff' }} />
              <Chip label={NODE_ROLE_LABELS[selectedNode.node_role] || selectedNode.node_role} size="small" variant="outlined" />
              <Chip label={selectedNode.status} size="small" color={selectedNode.status === 'active' ? 'success' : 'default'} />
            </Box>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2"><strong>Ledgers:</strong> {selectedNode.ledger_count}</Typography>
            <Typography variant="body2"><strong>Legal Identities:</strong> {selectedNode.legal_identity_count}</Typography>
            {selectedNode.bound_identity && (
              <Chip label="Has Bound Identity" size="small" color="warning" sx={{ mt: 0.5 }} />
            )}
            {selectedNode.pact_node_id && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                <strong>PACT ID:</strong> {selectedNode.pact_node_id}
              </Typography>
            )}
            {selectedNode.pact_sync_status === 'synced' && (
              <Chip label="PACT Synced" size="small" color="success" sx={{ mt: 0.5 }} />
            )}
            {selectedNode.pact_sync_status === 'pending' && (
              <Chip label="PACT Pending" size="small" color="warning" sx={{ mt: 0.5 }} />
            )}
            {selectedNode.pact_sync_status === 'error' && (
              <Chip label="PACT Error" size="small" color="error" sx={{ mt: 0.5 }} />
            )}
            {selectedNode.api_url && (
              <Typography variant="body2">
                <strong>API URL:</strong> {selectedNode.api_url}
              </Typography>
            )}
            <Box mt={2}>
              <Button
                variant="contained"
                size="small"
                endIcon={<OpenInNewIcon />}
                onClick={() => {
                  setDrawerOpen(false);
                  navigate(`/node-network/nodes/${selectedNode.id}`);
                }}
                fullWidth
              >
                Open Full Detail
              </Button>
            </Box>
          </>
        )}
      </Drawer>
    </Box>
  );
}

export default NodeNetworkGraph;
